import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, Observable, throwError } from "rxjs";
import { catchError, map, shareReplay, tap } from "rxjs/operators";
import { AuthenticationService } from "src/app/_services/authentication.service";
import { StateService } from "src/app/_services/state.service";
import { RequestsService } from "src/app/_shared/services/requests.service";
import { formatDatesRanges } from "src/app/_shared/utils/formatDates";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";
import { IServiceOrder } from "../interface";
import {
  fieldTypes,
  ILoaders,
  IOrderFilter,
  IServiceList,
  IServiceOrderState,
  ServiceOrderState,
  sortDirection,
  SortType,
} from "./interface";
import { ServiceStateService } from "./service-state.service";

const initialState: ServiceOrderState = {
  serviceOrders: {
    data: [],
    limit: 20,
    page: 1,
    isLoading: false,
    total: 0,
    selectedOrderId: undefined,
    filters: {
      farmer_name: "",
      service_name: "",
      status: "",
      dateRange: null,
    },
    /**
     * service order sort type
     */
    sortType: null,
  },
  serviceProviderPricing: null,
  loaders: {
    singleOrder: false,
    singleSoilTestOrder: false,
    createOrder: false,
    uploadingResults: false,
  },
  errors: {},
  success: {},
};

export enum ServiceType {
  SERVICE_ORDER = "orders",
  SOIL_TEST_ORDER = "soil-testing",
  FARM_PLAN_ORDER = "farm-plan",
  GARDEN_MAPPING_ORDER = "garden-mapping",
}

const serviceTypeMapper = {
  [ServiceType.SERVICE_ORDER]: "service-orders",
  [ServiceType.SOIL_TEST_ORDER]: "soil-testing-orders",
  [ServiceType.FARM_PLAN_ORDER]: "farm-plan-orders",
  [ServiceType.GARDEN_MAPPING_ORDER]: "garden-mapping-orders",
};

export const serviceTypes = Object.values(ServiceType);
declare var $: any;

@Injectable({
  providedIn: "root",
})
export class ServiceOrderService extends StateService<ServiceOrderState> {
  currentServiceOrders$: Observable<IServiceOrderState> = this.select(
    (state) => {
      return state.serviceOrders;
    }
  );

  filteredServiceOrders$: Observable<IServiceOrder[]> =
    this.currentServiceOrders$.pipe(
      map((state) => {
        return filterResults(state.data, state.filters);
      })
    );

  sortType$: Observable<SortType> = this.currentServiceOrders$.pipe(
    map((state) => {
      return state.sortType;
    })
  );

  sortedServiceOrders$: Observable<IServiceOrder[]> = combineLatest(
    this.filteredServiceOrders$,
    this.sortType$
  ).pipe(
    map(([orders, sorting]) => {
      if (sorting) {
        return sortServiceOrders(orders ? orders : [], sorting);
      }
      return orders;
    }),
    shareReplay(1)
  );

  ordersAmountTotal$: Observable<number> = this.filteredServiceOrders$.pipe(
    map((state) => {
      function isNumeric(value) {
        return /^-?\d+$/.test(value);
      }
      return state.reduce((prev, curr) => {
        if (isNumeric(curr.total_cost)) {
          return prev + parseInt(curr.total_cost as string);
        }
        return prev;
      }, 0);
    }),
    shareReplay(1)
  );

  uniqueFarmers$: Observable<number> = this.filteredServiceOrders$.pipe(
    map((state) => {
      var uniqs = state.reduce((acc, val) => {
        acc[val.farmer_contact] =
          acc[val.farmer_contact] === undefined
            ? 1
            : (acc[val.farmer_contact] += 1);
        return acc;
      }, {});
      return Object.keys(uniqs).length;
    }),
    shareReplay(1)
  );
  paginatedOrders$: Observable<IServiceOrder[]> =
    this.sortedServiceOrders$.pipe(
      map((orders) => {
        return paginate(
          orders,
          this.state.serviceOrders.limit,
          this.state.serviceOrders.page
        );
      })
    );
  orderPage$: Observable<any> = this.currentServiceOrders$.pipe(
    map((orders) => orders.page)
  );
  orderLimit$: Observable<any> = this.currentServiceOrders$.pipe(
    map((orders) => orders.limit)
  );

  loaders$: Observable<Partial<ILoaders>> = this.select((state) => {
    return state.loaders;
  });

  errors$: Observable<Partial<ILoaders>> = this.select((state) => {
    return state.errors;
  });
  success$: Observable<Partial<ILoaders>> = this.select((state) => {
    return state.success;
  });
  selectedOrder$: Observable<IServiceOrder> = this.select((state) => {
    return state.serviceOrders.selectedOrder;
  }).pipe(
    // Multicast to prevent multiple executions due to multiple subscribers
    shareReplay(1)
  );

  orderFilter$: Observable<IOrderFilter> = this.select(
    (state) => state.serviceOrders.filters
  );

  private serviceName = new BehaviorSubject<ServiceType>(
    ServiceType.SERVICE_ORDER
  );

  serviceList$: Observable<IServiceList[]> = this.select((state) => {
    const services = state.serviceOrders.data
      .map((item) => item.service_name)
      .filter((item, i, ar) => ar.indexOf(item) === i);
    return services.map((value) => {
      return { value, label: value };
    });
  });

  public sortOptions: SortType[] = [
    {
      field: "farmer_name",
      sort: sortDirection.ASC,
      fieldType: fieldTypes.STRING,
      label: "Name(A-Z)",
    },
    {
      field: "farmer_name",
      sort: sortDirection.DESC,
      fieldType: fieldTypes.STRING,
      label: "Name(Z-A)",
    },
    {
      field: "date",
      sort: sortDirection.DESC,
      fieldType: fieldTypes.DATE,
      label: "Recent",
    },
    {
      field: "date",
      sort: sortDirection.ASC,
      fieldType: fieldTypes.DATE,
      label: "Oldest",
    },
  ];

  constructor(
    private requestService: RequestsService,
    private authenticationService: AuthenticationService,
    private serviceState: ServiceStateService,
    private http: HttpClient
  ) {
    super(initialState);
  }

  setOrderType(type: ServiceType) {
    this.serviceName.next(type);
    this.loadServiceOrders({});
  }

  loadServiceOrders(obj = {}) {
    const params = new URLSearchParams(obj).toString();
    const endpoint = "service_orders/fetch_all_service_orders";
    this.setState({
      serviceOrders: { ...this.state.serviceOrders, isLoading: true },
    });
    // this.requestService.dash_get3(`${this.route}?${params}`, null, true).subscribe(
    this.requestService
      .dash_get3(`${endpoint}?${params}`, null, true)
      .subscribe(
        (res: any) => {
          // console.log(res);
          this.setState({
            serviceOrders: {
              ...this.state.serviceOrders,
              // data: res.data,
              data: res,
              isLoading: false,
              // total: res.total,
              total: 180,
            },
          });
        },
        (err) => {
          this.setState({
            serviceOrders: { ...this.state.serviceOrders, isLoading: false },
          });
        }
      );
  }

  deleteOrder(id: string, single?: boolean) {
    if (single) {
      this.setState({ loaders: { ...this.state.loaders, singleOrder: true } });
    }
    const updatedOrder = {
      ...this.state.serviceOrders.selectedOrder,
      isLoading: true,
    };
    this.setState({
      serviceOrders: {
        ...this.state.serviceOrders,
        data: this.state.serviceOrders.data.map((item) =>
          item.id === id ? updatedOrder : item
        ),
      },
    });

    this.requestService.dash_delete(id, `${this.route}`).subscribe(
      (res: any) => {
        this.setState({
          serviceOrders: {
            ...this.state.serviceOrders,
            data: this.state.serviceOrders.data.filter(
              (item) => item.id !== id
            ),
          },
        });
        Swal.fire({
          title: "Order deleted successfully",
          type: "success",
          timer: 2000,
        });
        $("#serviceOrderDetailsModal").modal("hide");
        if (single) {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
          });
        }
      },
      (_err) => {
        this.setState({
          serviceOrders: {
            ...this.state.serviceOrders,
            data: this.state.serviceOrders.data.map((item) =>
              item.id === id ? this.state.serviceOrders.selectedOrder : item
            ),
          },
        });
        if (single) {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
          });
        }
        Swal.fire({
          title: "Deleting order failed",
          type: "error",
          text: "Please try again",
          timer: 2000,
        });
      }
    );
  }

  private get route() {
    const route = serviceTypeMapper[this.serviceName.value];
    return route;
  }
  downloadServices(obj?: any) {
    const params = new URLSearchParams(obj).toString();
    return this.requestService.dash_get(
      `${this.route}/download?${params}`,
      null,
      true
    );
  }

  completeOrder(id: string) {
    this.setState({ loaders: { ...this.state.loaders, singleOrder: true } });
    this.requestService
      .dash_put(
        `${this.route}/${id || this.state.serviceOrders.selectedOrder.id}`,
        null
      )
      .subscribe(
        (res: any) => {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
            serviceOrders: {
              ...this.state.serviceOrders,
              selectedOrder: {
                ...this.state.serviceOrders.selectedOrder,
                status: "COMPLETED",
              },
              data: this.state.serviceOrders.data.map((item) =>
                item.id === id
                  ? {
                      ...this.state.serviceOrders.selectedOrder,
                      status: "COMPLETED",
                    }
                  : item
              ),
            },
          });
          Swal.fire({
            title: "Order Completed successfully",
            type: "success",
            timer: 2000,
          });
        },
        (err) => {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
          });
          Swal.fire({
            title: "Completing order failed",
            type: "error",
            text: "Please try again",
            timer: 2000,
          });
        }
      );
  }

  uploadSoilTestingResults(id: string, formData: FormData) {
    this.setState({
      loaders: { ...this.state.loaders, singleSoilTestOrder: true },
    });
    this.requestService
      .dash_putWithImage(`${this.route}/${id}/soiltesting`, formData, true)
      .subscribe(
        (_res) => {
          this.setState({
            serviceOrders: {
              ...this.state.serviceOrders,
              selectedOrder: {
                ...this.state.serviceOrders.selectedOrder,
                status: "COMPLETED",
              },
              data: this.state.serviceOrders.data.map((item) =>
                item.id === id
                  ? {
                      ...this.state.serviceOrders.selectedOrder,
                      status: "COMPLETED",
                    }
                  : item
              ),
            },
            loaders: { ...this.state.loaders, singleSoilTestOrder: false },
          });
          Swal.fire({
            title: "Soil Testing results uploaded successfully",
            type: "success",
            timer: 2000,
          });
        },
        (err) => {
          this.setState({
            loaders: { ...this.state.loaders, singleSoilTestOrder: false },
          });
          Swal.fire({
            title: "Soil testing results upload failed",
            type: "error",
            text: err.error.message,
            timer: 2000,
          });
        }
      );
  }

  uploadOrderResults(id: string, formData: FormData) {
    this.setState({
      loaders: { ...this.state.loaders, uploadingResults: true },
    });
    return this.http
      .post(
        `${environment.API_URL}api/v2/${this.route}/${id}/results`,
        formData
      )
      .pipe(
        tap((_res: any) => {
          this.setState({
            serviceOrders: {
              ...this.state.serviceOrders,
              selectedOrder: {
                ...this.state.serviceOrders.selectedOrder,
                status: "COMPLETED",
                report: _res.report,
              },
              data: this.state.serviceOrders.data.map((item) =>
                item.id === id
                  ? {
                      ...this.state.serviceOrders.selectedOrder,
                      status: "COMPLETED",
                      report: _res.report,
                    }
                  : item
              ),
            },
            loaders: { ...this.state.loaders, uploadingResults: false },
          });
          Swal.fire({
            title: "Order Report successfully uploaded",
            type: "success",
            timer: 2000,
          });
        }),
        catchError((err) => {
          this.setState({
            loaders: { ...this.state.loaders, uploadingResults: false },
          });
          Swal.fire({
            title: "Order Report upload failed",
            type: "error",
            text: err.error.message,
            timer: 2000,
          });
          return throwError(err);
        })
      );
  }

  public set currentOrdersPage(v: number) {
    this.setState({ serviceOrders: { ...this.state.serviceOrders, page: v } });
  }

  selectOrder(order: IServiceOrder) {
    this.setState({
      serviceOrders: {
        ...this.state.serviceOrders,
        selectedOrder: order,
      },
    });
  }

  updateFilter(filter: Partial<IOrderFilter>) {
    this.setState({
      serviceOrders: {
        ...this.state.serviceOrders,
        filters: {
          ...this.state.serviceOrders.filters,
          ...filter,
        },
      },
    });
  }

  changeServiceOrderSort(sortType: SortType) {
    this.setState({
      serviceOrders: {
        ...this.state.serviceOrders,
        sortType,
      },
    });
  }

  updateOrder(id: string, update: Partial<IServiceOrder>) {
    this.setState({ loaders: { ...this.state.loaders, singleOrder: true } });
    this.requestService
      .dash_put(
        `${this.route}/${id || this.state.serviceOrders.selectedOrder.id}`,
        update
      )
      .subscribe(
        (res: any) => {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
            serviceOrders: {
              ...this.state.serviceOrders,
              selectedOrder: {
                ...this.state.serviceOrders.selectedOrder,
                status: update.status,
              },
              data: this.state.serviceOrders.data.map((item) =>
                item.id === id
                  ? {
                      ...this.state.serviceOrders.selectedOrder,
                      status: update.status,
                    }
                  : item
              ),
            },
          });
          Swal.fire({
            title: "Order updated successfully",
            type: "success",
            timer: 2000,
          });
        },
        (err) => {
          this.setState({
            loaders: { ...this.state.loaders, singleOrder: false },
          });
          Swal.fire({
            title: "Order update failed",
            type: "error",
            text: "Please try again",
            timer: 2000,
          });
        }
      );
  }
}

function paginate(array: any[], page_size: number, page_number: number) {
  let page = page_number || 1,
    per_page = page_size || 10,
    offset = (page - 1) * per_page,
    paginatedItems = array.slice(offset).slice(0, page_size);
  return paginatedItems;
}

function toUnix(param: string = null) {
  if (param) {
    return new Date(param).getTime();
  }
  return new Date().getTime();
}
function filterResults(
  array: IServiceOrder[],
  filter: IOrderFilter
): IServiceOrder[] {
  return array.filter((item) => {
    const checks = [];
    for (var key in filter) {
      if (filter[key] && key === "farmer_name") {
        const filterValue = filter[key].toLowerCase().trim();
        let check =
          (item.farmer_name || "").toLowerCase().trim().indexOf(filterValue) >
            -1 ||
          (item.farmer_district || "")
            .toLowerCase()
            .trim()
            .indexOf(filterValue) > -1;
        checks.push(check);
      } else if (
        Array.isArray(filter[key]) &&
        filter[key].length &&
        key === "dateRange"
      ) {
        let date: any = toUnix();
        const { createdAt__gte: startDate, createdAt__lte: endDate } =
          formatDatesRanges(filter[key]) as any;
        try {
          date = toUnix(item.date);
        } catch (error) {
          console.log("error of date", item);
        }
        checks.push(date >= toUnix(startDate) && date <= toUnix(endDate));
      } else if (
        filter[key] &&
        (item[key] === undefined ||
          item[key].toLowerCase() != filter[key].toLowerCase())
      ) {
        checks.push(false);
      }
    }
    checks.push(true);
    return checks.every((val) => val);
  });
}

function sortServiceOrders(
  array: IServiceOrder[],
  filter: SortType
): IServiceOrder[] {
  return array.sort((a, b) => {
    let objectA: any = null;
    let objectB: any = null;
    [objectA, objectB] = [a[filter.field], b[filter.field]];
    if (filter.field === fieldTypes.DATE) {
      try {
        objectA = toUnix(objectA);
        objectB = toUnix(objectB);
      } catch (error) {
        console.log("error of date", a, b);
      }
    }
    if (objectA < objectB) return -filter.sort;
    if (objectA > objectB) return filter.sort;
    return 0;
  });
}
